import React, { useState, useMemo, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import EncabezadoSST from './EncabezadoSST';
import SeccionPreguntasSST from './SeccionPreguntasSST';

// ==========================================
// COMPONENTE: PIZARRA DE FIRMA (CANVAS)
// ==========================================
function FirmaPad({ onSave, onClear }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#1e293b"; 
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    onSave(canvasRef.current.toDataURL("image/png"));
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onClear();
  };

  return (
    <div className="flex flex-col items-center gap-3 w-full my-4">
      <div className="bg-white/60 backdrop-blur-md border border-slate-300 rounded-2xl shadow-inner overflow-hidden w-full max-w-[400px] h-[180px] relative touch-none">
        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <div className="absolute top-3 left-4 text-[10px] font-black text-slate-400 uppercase tracking-widest pointer-events-none select-none">
          Firma del Evaluador
        </div>
      </div>
      <button 
        onClick={handleClear}
        type="button"
        className="text-[10px] uppercase tracking-wider font-black text-slate-500 hover:text-red-500 transition-colors cursor-pointer bg-white/50 px-4 py-1.5 rounded-full border border-slate-200 shadow-sm"
      >
        Limpiar Firma
      </button>
    </div>
  );
}

function SafePortalModal({ children }) {
  const elRef = useRef(null);
  if (!elRef.current && typeof document !== "undefined") {
    const div = document.createElement("div");
    div.setAttribute("data-portal-modal", "true");
    div.setAttribute("translate", "no");
    elRef.current = div;
  }
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    document.body.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);
  if (!elRef.current) return null;
  return ReactDOM.createPortal(children, elRef.current);
}

const SECCIONES_INSPECCION = {
  "CONDICIONES LOCATIVAS": {
    icono: "🏗️",
    preguntas: [
      { id: "loc_1", texto: "Cuenta con concepto sanitario" },
      { id: "loc_2", texto: "Estado general de techos, paredes, pisos, puertas, ventanas, vidrios, y fachadas" },
      { id: "loc_3", texto: "Iluminación adecuada en todas las áreas" },
      { id: "loc_4", texto: "Ventilación adecuada (natural o mecánica)" },
      { id: "loc_5", texto: "Estado de gondolas, estanterias, anaqueles y mobiliario" },
      { id: "loc_6", texto: "Protección contra caídas (barandas, escaleras seguras, pisos y/o cintas antideslizantes)" },
      { id: "loc_7", texto: "Hay red de internet para los equipos tecnologicos" },
      { id: "loc_8", texto: "Se cuenta con recurso hidroelectrico" },
      { id: "loc_9", texto: "Se cuenta con agua potable" },
      { id: "loc_10", texto: "Las áreas de trabajo cumplen con el nivel de confort de ruido" }
    ]
  },
  "ORDEN Y ASEO": {
    icono: "🧹",
    preguntas: [
      { id: "ord_1", texto: "Estado general de aseo en el PDV" },
      { id: "ord_2", texto: "Pasillos libres de obstáculos" },
      { id: "ord_3", texto: "Mercancía correctamente ubicada en las estanterías" },
      { id: "ord_4", texto: "Estanterías libres de suciedad y correctamente higienizadas" },
      { id: "ord_5", texto: "Vigencia de certificados de fumigacion y control de plagas" },
      { id: "ord_6", texto: "Puntos ecologicos con bolsa según codigo de color, tapa y clasificación de residuos" },
      { id: "ord_7", texto: "Vigencia certificado limpieza y desinfeccion de tanques de almacenamiento de agua potable" },
      { id: "ord_8", texto: "Higiene personal (dotación, epp, uñas, cabello, maquillaje y accesorios)" },
      { id: "ord_9", texto: "Los cuadros y/o avisos estan limpios y firmes" },
      { id: "ord_10", texto: "Los equipos de computo estan limpios y ubicados correctamente" },
      { id: "ord_11", texto: "El área de vestidores y/o descanso del personal estan debidamente dotada y limpia" },
      { id: "ord_12", texto: "Los Baños estan debidamente dotados y limpios" },
      { id: "ord_13", texto: "Áreas de cajas y servicio al cliente libres de desorden" },
      { id: "ord_14", texto: "Los elementos de aseo se encuentran limpios (traperos, escobas, trapos, etc)" },
      { id: "ord_15", texto: "Carros y canastillas higienizados y en buen estado (sin ruedas dañadas ni estructuras rotas)" },
      { id: "ord_16", texto: "Vertimientos generados por refigeradores, congeladores o tuberias" },
      { id: "ord_17", texto: "Orden y limpieza de las carpetas y documentación" }
    ]
  },
  "MANTENIMIENTO": {
    icono: "🛠️",
    preguntas: [
      { id: "man_1", texto: "Funcionamiento adecuado de refigeradores y congeladores (temperatura)" },
      { id: "man_2", texto: "Sellado hermético de puertas y tapas de refrigeradores y/o congeladores" },
      { id: "man_3", texto: "Termómetros calibrados" },
      { id: "man_4", texto: "Ventiladores y ductos libres de polvo y residuos" },
      { id: "man_5", texto: "Equipos de corte (cuchillos, rebanadoras, sierras) afilados y en óptimas condiciones" },
      { id: "man_6", texto: "Herramientas de trabajo en buen estado (sin desgaste excesivo, sin piezas rotas)" },
      { id: "man_7", texto: "Balanzas calibradas y operativas" }
    ]
  },
  "PRODUCTOS QUIMICOS": {
    icono: "🧪",
    preguntas: [
      { id: "qui_1", texto: "Todos los productos químicos cuentan con su etiqueta original" },
      { id: "qui_2", texto: "Productos químicos almacenados en áreas separadas de alimentos y artículos de consumo" },
      { id: "qui_3", texto: "Fichas técnicas de productos quimicos" },
      { id: "qui_4", texto: "Las areas de almacenamiento de productos quimicos estan señalizadas" }
    ]
  },
  "SIS. ELECTRICO": {
    icono: "⚡",
    preguntas: [
      { id: "ele_1", texto: "Los cables estan canalizados, protegidos y bien fijados" },
      { id: "ele_2", texto: "Los tableros y paneles electricos estan señalizados y tienen facil acceso" }
    ]
  },
  "CONDICIONES DE SEGURIDAD": {
    icono: "🦺",
    preguntas: [
      { id: "seg_1", texto: "Se cuenta con extintores suficientes en el PDV (1 cada 200 m²)" },
      { id: "seg_2", texto: "Los extintores estan ubicados en su soporte, señalizados y con facil acceso" },
      { id: "seg_3", texto: "Vigencia y presurización del extintor" },
      { id: "seg_4", texto: "El tipo de extintor es acorde al riesgo presente" },
      { id: "seg_5", texto: "Se cuenta con botiquin portatil tipo A" },
      { id: "seg_6", texto: "Los elementos del botiquin estan vigentes y en buen estado" },
      { id: "seg_7", texto: "El botiquin esta ubicado en un lugar visible, de facil acceso y señalizado" },
      { id: "seg_8", texto: "Camilla de emergencias en lugar visible, de facil acceso y señalizada" },
      { id: "seg_9", texto: "La araña de transporte de la camilla se encuentra en buen estado" },
      { id: "seg_10", texto: "Se cuenta con señalizacion y demarcación de salidas de emergencia y rutas de evacuacion" },
      { id: "seg_11", texto: "Existe señalización de espacios libres de humo" },
      { id: "seg_12", texto: "Existe señalización de control y monitoreo por camaras de seguridad" },
      { id: "seg_13", texto: "Los brigadistas del PDV estan identificados con chaleco" },
      { id: "seg_14", texto: "Los formatos manejados en el PDV se encuentran al día" },
      { id: "seg_15", texto: "Los documentos pertinentes a SST se encuentran al dia (certificados, examenes)" },
      { id: "seg_16", texto: "Se presentaron accidentes laborales durante el mes" },
      { id: "seg_17", texto: "Se reportaron enfermedades laborales durante el mes" }
    ]
  }
};

const SECCION_REPORTE_MANTENIMIENTO = {
  titulo: "REPORTE DE NOVEDADES DE MANTENIMIENTO",
  icono: "🔧",
  esOpcional: true,
  preguntas: [
    { id: "rep_mant_1", texto: "Se requiere intervención correctiva en paredes o muros (filtraciones, humedad o grietas)" },
    { id: "rep_mant_2", texto: "Daños o fallas en luminarias, interruptores o cableado eléctrico expuesto" },
    { id: "rep_mant_3", texto: "Problemas en sistemas hidrosanitarios (fugas en tuberías, grifería o desagües)" },
    { id: "rep_mant_4", texto: "Deterioro en puertas, chapas, ventanas o vidrios que comprometan la seguridad" }
  ]
};

export default function InspeccionSST({ usuario, onBack }) {
  const nombreInspector = usuario?.nombres || usuario?.nombre || '';

  const [datosGenerales, setDatosGenerales] = useState({ 
    pdv: "", 
    unidadNegocio: "EL GRAN SURTIDOR", 
    fecha: new Date().toISOString().split('T')[0], 
    inspector: nombreInspector 
  });
  
  const [listaSedes, setListaSedes] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [observaciones, setObservaciones] = useState({});
  const [evidenciasFotos, setEvidenciasFotos] = useState({});
  const [firmaBase64, setFirmaBase64] = useState(null);
  const [customAlert, setCustomAlert] = useState({ show: false, message: "", type: "success" });

  const totalPreguntas = 57;

  useEffect(() => {
    const fetchSedes = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL;
        const res = await fetch(`${API_URL}/api/sedes`);
        const data = await res.json();
        if (data.success && Array.isArray(data.sedes)) {
          setListaSedes(data.sedes);
        }
      } catch (error) {
        console.error("Error al cargar sedes maestras:", error);
      }
    };
    fetchSedes();
  }, []);

  const handleResponder = (seccion, idPregunta, valor) => {
    setRespuestas(prev => ({ ...prev, [idPregunta]: valor }));
  };

  const handleObservacion = (seccion, idPregunta, texto) => {
    setObservaciones(prev => ({ ...prev, [idPregunta]: texto }));
  };

  const handleFotoPregunta = (idPregunta, base64) => {
    setEvidenciasFotos(prev => ({ ...prev, [idPregunta]: base64 }));
  };

  const estadisticas = useMemo(() => {
    let totalResp = 0;
    let totalPuntos = 0;
    const promediosPorSeccion = {};

    Object.keys(SECCIONES_INSPECCION).forEach(seccion => {
      let sumaSeccion = 0;
      let cuentaSeccion = 0;

      SECCIONES_INSPECCION[seccion].preguntas.forEach(p => {
        if (respuestas[p.id] !== undefined) {
          sumaSeccion += respuestas[p.id];
          cuentaSeccion++;
          totalResp++;
          totalPuntos += respuestas[p.id];
        }
      });

      promediosPorSeccion[seccion] = cuentaSeccion > 0 ? (sumaSeccion / cuentaSeccion) * 100 : 0;
    });

    return {
      porcentajeGeneral: totalResp > 0 ? (totalPuntos / totalResp) * 100 : 0,
      promedios: promediosPorSeccion
    };
  }, [respuestas]);

  const handleSubmit = async () => {
    if (!datosGenerales.pdv || !datosGenerales.pdv.trim()) {
      setCustomAlert({ show: true, message: "Por favor, selecciona el Punto de Venta (PDV).", type: "warning" });
      return;
    }
    if (Object.keys(respuestas).length === 0) {
      setCustomAlert({ show: true, message: "No has respondido ninguna pregunta de la inspección.", type: "warning" });
      return;
    }
    if (!firmaBase64) {
      setCustomAlert({ show: true, message: "La firma del evaluador es obligatoria para guardar el formulario.", type: "warning" });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const API_URL = process.env.REACT_APP_API_URL;

      const bodyPayload = {
        tipo_formulario: "INSPECCION_SST",
        datosGenerales: {
          pdv: datosGenerales.pdv.trim(),
          unidadNegocio: datosGenerales.unidadNegocio,
          fecha: datosGenerales.fecha,
          inspector: datosGenerales.inspector
        },
        respuestas: respuestas,
        observaciones: observaciones,
        evidencias_fotos: evidenciasFotos,
        puntaje_global: estadisticas.porcentajeGeneral,
        evidencias: {
          firma_evaluador: firmaBase64
        }
      };

      const res = await fetch(`${API_URL}/api/formularios/guardar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(bodyPayload)
      });

      const data = await res.json();
      if (data.success) {
        setCustomAlert({ show: true, message: data.message || "¡Inspección guardada exitosamente!", type: "success" });
        setTimeout(() => {
          onBack(); 
        }, 1500);
      } else {
        setCustomAlert({ show: true, message: data.message || "Error al guardar la inspección.", type: "error" });
      }
    } catch (error) {
      console.error("Error de conexión:", error);
      setCustomAlert({ show: true, message: "Error de conexión con el servidor al intentar guardar.", type: "error" });
    }
  };

  return (
    <div className="bg-white/20 backdrop-blur-3xl rounded-2xl sm:rounded-[34px] shadow-2xl max-w-5xl w-full mx-auto border border-white/30 animate-fade-in relative h-[90vh] overflow-hidden flex flex-col">
      
      {/* CABECERA */}
      <div className="bg-transparent border-b border-white/20 px-4 py-3 sm:px-8 sm:py-5 z-40 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <button onClick={onBack} className="w-10 h-10 flex-none bg-white/60 hover:bg-white/80 text-slate-700 hover:text-red-500 flex items-center justify-center rounded-full transition-all shadow-sm border border-white/40 cursor-pointer backdrop-blur-sm" title="Cerrar Formulario">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <h1 className="text-lg sm:text-xl font-black text-slate-800 leading-tight">Inspección SG-SST</h1>
            <span className="bg-white/60 text-blue-800 border border-white/50 text-[10px] font-bold px-2 py-0.5 rounded-md backdrop-blur-sm">FT-SST-06</span>
          </div>
        </div>
        
        <div className="bg-slate-900/80 backdrop-blur-md rounded-2xl p-2 px-5 shadow-inner flex items-center justify-between gap-4 text-white w-full sm:w-auto border border-slate-700/50">
          <div className="text-center">
            <p className="text-[8px] text-slate-300 font-bold uppercase tracking-widest mb-0.5">Avance</p>
            <p className="text-xs sm:text-sm font-black text-blue-400">
              {Object.keys(respuestas).filter(k => !k.startsWith("rep_mant_")).length} <span className="text-slate-500 text-[10px]">/ {totalPreguntas}</span>
            </p>
          </div>
          <div className="w-px h-6 bg-slate-600/50"></div>
          <div className="text-center">
            <p className="text-[8px] text-slate-300 font-bold uppercase tracking-widest mb-0.5">% Cumpl</p>
            <p className={`text-xs sm:text-sm font-black leading-none ${estadisticas.porcentajeGeneral >= 90 ? 'text-emerald-400' : estadisticas.porcentajeGeneral >= 70 ? 'text-yellow-400' : 'text-red-400'}`}>
              {estadisticas.porcentajeGeneral.toFixed(1)}%
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 sm:p-8 w-full relative">
        <EncabezadoSST datos={datosGenerales} setDatos={setDatosGenerales} listaSedes={listaSedes} />

        <div className="space-y-4">
          {Object.entries(SECCIONES_INSPECCION).map(([nombreSeccion, info]) => (
            <div key={nombreSeccion} className="relative mt-2">
              <div className="absolute -top-3 right-4 z-10 bg-slate-800 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-md border border-slate-600">
                {estadisticas.promedios[nombreSeccion].toFixed(1)}%
              </div>
              
              <SeccionPreguntasSST 
                titulo={nombreSeccion} 
                icono={info.icono}
                preguntas={info.preguntas}
                respuestas={respuestas}
                observaciones={observaciones}
                evidenciasFotos={evidenciasFotos}
                onResponder={handleResponder}
                onObservacion={handleObservacion}
                onFotoPregunta={handleFotoPregunta}
              />
            </div>
          ))}

          {/* SECCIÓN MANTENIMIENTO */}
          <div className="relative mt-6 bg-white/70 backdrop-blur-xl rounded-[28px] border border-white/80 shadow-md p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-200/60 pb-4 mb-5 gap-2">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🔧</span>
                <div>
                  <h3 className="font-black text-slate-800 text-base uppercase tracking-wider">{SECCION_REPORTE_MANTENIMIENTO.titulo}</h3>
                  <p className="text-xs text-slate-500 font-bold mt-0.5">Módulo opcional e informativo (No afecta el porcentaje general de cumplimiento del SG-SST)</p>
                </div>
              </div>
              <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-[10px] font-black uppercase tracking-wider shrink-0 w-fit">Opcional / Novedades</span>
            </div>

            <div className="space-y-4">
              {SECCION_REPORTE_MANTENIMIENTO.preguntas.map((item) => {
                const estadoMantenimiento = respuestas[item.id]; 
                const tieneNovedad = estadoMantenimiento === "NO_CUMPLE";
                const fotoActual = evidenciasFotos[item.id];

                return (
                  <div key={item.id} className="bg-white/90 rounded-2xl p-4 sm:p-5 border border-slate-200/80 shadow-2xs space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <span className="text-xs font-black text-slate-800 flex-1">{item.texto}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => handleResponder("MANTENIMIENTO_NOVEDADES", item.id, "CUMPLE")}
                          className={`px-4 py-2 rounded-xl text-xs font-black transition cursor-pointer ${estadoMantenimiento === "CUMPLE" ? "bg-emerald-600 text-white shadow-md scale-105" : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200"}`}
                        >
                          ✓ Sin Novedad / Bueno
                        </button>
                        <button
                          type="button"
                          onClick={() => handleResponder("MANTENIMIENTO_NOVEDADES", item.id, "NO_CUMPLE")}
                          className={`px-4 py-2 rounded-xl text-xs font-black transition cursor-pointer ${estadoMantenimiento === "NO_CUMPLE" ? "bg-amber-600 text-white shadow-md scale-105" : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200"}`}
                        >
                          ⚠ Reportar Novedad
                        </button>
                      </div>
                    </div>

                    {tieneNovedad && (
                      <div className="pt-3 mt-3 border-t border-slate-200/80 space-y-3 animate-fade-in">
                        <textarea
                          rows="2"
                          placeholder="Describa detalladamente la novedad de mantenimiento..."
                          value={observaciones[item.id] || ""}
                          onChange={(e) => handleObservacion("MANTENIMIENTO_NOVEDADES", item.id, e.target.value)}
                          className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 shadow-inner resize-none"
                        />

                        <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200">
                          <div className="flex items-center gap-2">
                            <span className="text-sm">📸</span>
                            <span className="text-[11px] font-bold text-slate-600">{fotoActual ? "Fotografía de evidencia adjunta ✅" : "Adjuntar registro fotográfico de la novedad"}</span>
                          </div>
                          
                          <label className="px-4 py-2 bg-blue-50 hover:bg-blue-600 text-blue-700 hover:text-white rounded-lg text-xs font-black transition cursor-pointer border border-blue-200 shadow-sm">
                            <span>{fotoActual ? "Cambiar Foto" : "Subir Foto"}</span>
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onloadend = () => handleFotoPregunta(item.id, reader.result);
                                  reader.readAsDataURL(file);
                                }
                              }}
                            />
                          </label>
                        </div>

                        {fotoActual && (
                          <div className="relative w-24 h-24 rounded-xl overflow-hidden border border-slate-300 shadow-sm">
                            <img src={fotoActual} alt="Evidencia Novedad" className="w-full h-full object-cover" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* FIRMA Y BOTÓN GUARDAR */}
        <div className="mt-8 border-t border-white/20 pt-8 pb-4 flex flex-col items-center">
          <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest mb-2">Validación y Cierre</h3>
          <p className="text-xs text-slate-600 font-medium mb-4 text-center">Por favor, registra tu firma en el recuadro inferior para certificar la inspección.</p>
          <FirmaPad onSave={(firmaBase64) => setFirmaBase64(firmaBase64)} onClear={() => setFirmaBase64(null)} />
          <button onClick={handleSubmit} className="w-full max-w-sm mt-6 bg-gradient-to-r from-blue-600/90 to-blue-500/90 hover:from-blue-600 hover:to-blue-500 backdrop-blur-md text-white font-black py-4 rounded-xl shadow-[0_8px_30px_rgba(37,99,235,0.3)] active:scale-95 transition-all text-sm tracking-widest uppercase cursor-pointer border border-blue-400/50">
            Guardar Inspección
          </button>
        </div>
      </div>

      {customAlert.show && (
        <SafePortalModal>
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex justify-center items-center p-4 z-[99999] animate-fade-in">
            <div className="bg-white/50 backdrop-blur-3xl rounded-[32px] p-6 sm:p-8 max-w-sm w-full text-center shadow-[0_30px_90px_rgba(0,0,0,0.2)] border border-white/80">
              <div className={`w-20 h-20 mx-auto rounded-3xl flex items-center justify-center mb-5 shadow-sm border border-white/80 backdrop-blur-md ${customAlert.type === 'success' ? 'bg-emerald-500/20 text-emerald-700' : customAlert.type === 'warning' ? 'bg-amber-500/20 text-amber-700' : 'bg-red-500/20 text-red-700'}`}>
                <span className="text-4xl">{customAlert.type === 'success' ? '✅' : customAlert.type === 'warning' ? '⚠️' : '❌'}</span>
              </div>
              <h3 className="text-xl font-extrabold text-slate-800 mb-2.5">{customAlert.type === 'success' ? '¡Éxito!' : customAlert.type === 'warning' ? 'Atención' : 'Error'}</h3>
              <p className="text-sm text-slate-700 font-medium mb-8 leading-relaxed px-2">{customAlert.message}</p>
              <button onClick={() => setCustomAlert({ ...customAlert, show: false })} className="w-full py-3.5 rounded-2xl font-bold text-white transition-all shadow-lg cursor-pointer bg-blue-600 hover:bg-blue-500">Entendido</button>
            </div>
          </div>
        </SafePortalModal>
      )}
    </div>
  );
}